import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import {
  getMisEntregas, marcarEnRuta, getDiaRepartoLabel, getFechaReparto, isAfterCutoff
} from '../../lib/entregasApi'
import { supabase } from '../../lib/supabase'
import {
  geocodificar, geocodearClientesPendientes,
  ordenarPorVecinoCercano, agruparEnParadas, distanciaTotal
} from '../../lib/geo'
import QRScanner from '../../components/QRScanner'

const VIVIENDA = { casa: '🏠 CASA', edificio: '🏢 EDIFICIO', oficina: '🏢 OFICINA' }

export default function RepartidorHoy() {
  const { user, profile } = useAuth()
  const nombreRepartidor = profile?.nombre?.split(' ')[0] || 'tu repartidor'
  const navigate = useNavigate()
  const [fecha, setFecha] = useState(getFechaReparto())
  const [entregas, setEntregas] = useState([])
  const [deposito, setDeposito] = useState(null)
  const [loading, setLoading] = useState(true)
  const [optimizando, setOptimizando] = useState(false)
  // 'en_ruta' = lo que tengo en la camioneta AHORA (default)
  // 'proximas' = asignadas pero todavia no cargue
  // 'hechas' = entregadas o no entregadas
  // 'todas' = todo
  const [filtro, setFiltro] = useState('en_ruta')
  const [enviando, setEnviando] = useState(false)
  const [showDeposito, setShowDeposito] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [scanError, setScanError] = useState(null)

  useEffect(() => {
    if (!user?.id) return
    cargarTodo()
  }, [user?.id, fecha])

  async function cargarTodo() {
    setLoading(true)
    const [{ data: dep }, ents] = await Promise.all([
      supabase.rpc('get_deposito'),
      getMisEntregas(user.id, fecha),
    ])
    const depo = dep?.[0]?.lat ? dep[0] : null
    setDeposito(depo)
    setEntregas(ents)
    setLoading(false)
    if (!depo) setShowDeposito(true)
  }

  async function optimizar() {
    if (!deposito?.lat) { setShowDeposito(true); return }
    const pendientes = entregas.filter(e => e.estado === 'programada' || e.estado === 'en_ruta')
    const sinGeo = pendientes.filter(e => !e.cliente?.lat)
    if (sinGeo.length === 0) return
    setOptimizando(true)
    try {
      const clientesUnicos = [...new Map(sinGeo.map(e => [e.cliente.id, e.cliente])).values()]
      await geocodearClientesPendientes(clientesUnicos)
      await cargarTodo()
    } finally { setOptimizando(false) }
  }

  async function salirARuta() {
    const programadas = entregas.filter(e => e.estado === 'programada').map(e => e.id)
    if (programadas.length === 0) return
    if (!confirm(`¿Salir a ruta con ${programadas.length} entrega${programadas.length !== 1 ? 's' : ''}? Después podés tocar "📲 Avisar al cliente" en cada parada.`)) return
    setEnviando(true)
    try {
      await marcarEnRuta(programadas)
      await cargarTodo()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setEnviando(false) }
  }

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  /** Procesa un QR escaneado: valida que sea una entrega tuya y navega al flujo de confirmación. */
  function onQRDetectado(text) {
    setShowScanner(false)
    setScanError(null)
    // Esperamos algo tipo https://limpack.uy/d/XXXXXX o /d/XXXXXX
    const match = String(text).match(/\/d\/([a-zA-Z0-9_-]+)/)
    if (!match) {
      setScanError('❌ Ese QR no es de una etiqueta Limpack.')
      setTimeout(() => setScanError(null), 5000)
      return
    }
    const token = match[1]
    const e = entregas.find(x => x.tracking_token === token)

    // 1. La entrega no está en MI ruta del día → bloquear (seguridad operativa)
    if (!e) {
      setScanError('⚠️ Esta entrega no está en tu ruta de hoy. Pedí a admin que te la asigne o revisá si es la etiqueta correcta.')
      setTimeout(() => setScanError(null), 6000)
      return
    }
    // 2. Ya entregada → avisar
    if (e.estado === 'entregada') {
      setScanError(`✅ ${(e.cliente?.beneficiario || e.cliente?.titular)?.nombre || 'Esta entrega'} ya está marcada como entregada.`)
      setTimeout(() => setScanError(null), 5000)
      return
    }
    // 3. Cancelada → avisar
    if (e.estado === 'cancelada') {
      setScanError('⚠️ Esta entrega fue cancelada. No corresponde entregarla.')
      setTimeout(() => setScanError(null), 5000)
      return
    }
    // OK: es mía y está pendiente → al flujo de confirmación
    navigate(`/d/${token}`)
  }

  // Filtrado + ordenamiento por ruta + agrupación en paradas
  const paradas = useMemo(() => {
    const filtradas =
      filtro === 'en_ruta'  ? entregas.filter(e => e.estado === 'en_ruta')
    : filtro === 'proximas' ? entregas.filter(e => e.estado === 'programada')
    : filtro === 'hechas'   ? entregas.filter(e => e.estado === 'entregada' || e.estado === 'no_entregada')
    :                          entregas
    // Anotar lat/lng del cliente en la entrega para los helpers
    const conGeo = filtradas.map(e => ({ ...e, lat: e.cliente?.lat, lng: e.cliente?.lng }))
    if (!deposito?.lat) {
      // Sin depósito → orden por ventana + barrio (orden natural del query)
      return [{ centroide: { lat: null, lng: null }, entregas: conGeo, label: '', numero: 1, distanciaDesdeAnterior: null }]
    }
    const ordenadas = ordenarPorVecinoCercano(deposito, conGeo)
    return agruparEnParadas(ordenadas, 80)
  }, [entregas, filtro, deposito])

  const counts = useMemo(() => ({
    enRuta:   entregas.filter(e => e.estado === 'en_ruta').length,
    proximas: entregas.filter(e => e.estado === 'programada').length,
    hechas:   entregas.filter(e => e.estado === 'entregada' || e.estado === 'no_entregada').length,
    todas:    entregas.length,
  }), [entregas])

  const programadasCount = counts.proximas
  const after = isAfterCutoff()

  // Próxima parada que tenga al menos una entrega activa (solo aplica al tab "En ruta")
  const proximaParada = useMemo(() => {
    if (filtro !== 'en_ruta') return null
    return paradas.find(p => p.entregas.some(e => e.estado === 'en_ruta'))
  }, [paradas, filtro])

  // Geo necesita refresco? (programadas y en_ruta van a necesitar lat/lng)
  const sinGeoCount = entregas.filter(e =>
    (e.estado === 'programada' || e.estado === 'en_ruta') && !e.cliente?.lat
  ).length

  return (
    <div className="rep-app">
      <header className="rep-head">
        <div>
          <div className="rep-brand">Limpack</div>
          <div className="rep-sub">Hola, <strong>{user?.email?.split('@')[0]}</strong></div>
        </div>
        <button className="rep-logout" onClick={logout}>Salir</button>
      </header>

      <div className="rep-fecha">
        <div className="rep-fecha-label">{after ? '🔒 Mañana' : '📦 Hoy'} — {getDiaRepartoLabel()}</div>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="rep-fecha-input" />
      </div>

      {/* Banda de configuración del depósito */}
      {!deposito?.lat && (
        <div className="rep-warn-bar" onClick={() => setShowDeposito(true)}>
          ⚠️ Configurá tu punto de partida para optimizar la ruta →
        </div>
      )}
      {deposito?.lat && sinGeoCount > 0 && (
        <div className="rep-info-bar">
          📍 {sinGeoCount} dirección{sinGeoCount !== 1 ? 'es' : ''} sin ubicar
          <button className="rep-link-btn" onClick={optimizar} disabled={optimizando}>
            {optimizando ? 'Buscando…' : 'Geocodear y optimizar →'}
          </button>
        </div>
      )}

      <div className="rep-tabs">
        <button className={`rep-tab ${filtro === 'en_ruta' ? 'active' : ''}`} onClick={() => setFiltro('en_ruta')}>
          En ruta <span className="rep-tab-num">{counts.enRuta}</span>
        </button>
        <button className={`rep-tab ${filtro === 'proximas' ? 'active' : ''}`} onClick={() => setFiltro('proximas')}>
          Próximas <span className="rep-tab-num">{counts.proximas}</span>
        </button>
        <button className={`rep-tab ${filtro === 'hechas' ? 'active' : ''}`} onClick={() => setFiltro('hechas')}>
          Hechas <span className="rep-tab-num">{counts.hechas}</span>
        </button>
        <button className={`rep-tab ${filtro === 'todas' ? 'active' : ''}`} onClick={() => setFiltro('todas')}>
          Todas <span className="rep-tab-num">{counts.todas}</span>
        </button>
      </div>

      {/* Botón salir a ruta */}
      {programadasCount > 0 && filtro !== 'hechas' && (
        <div className="rep-action-bar">
          <button className="rep-btn-ruta" onClick={salirARuta} disabled={enviando}>
            🚐 Salir a ruta ({programadasCount})
          </button>
        </div>
      )}

      {/* HERO: próxima parada */}
      {proximaParada && (
        <ProximaParadaCard parada={proximaParada} deposito={deposito} navigate={navigate} nombreRepartidor={nombreRepartidor} />
      )}

      {/* Lista de todas las paradas */}
      {loading ? (
        <div className="rep-empty">Cargando…</div>
      ) : paradas.length === 0 || (paradas.length === 1 && paradas[0].entregas.length === 0) ? (
        <div className="rep-empty">
          {filtro === 'en_ruta' && (programadasCount > 0
            ? '📦 Cargá tu camioneta y tocá "Salir a ruta" arriba.'
            : '🎉 No tenés entregas en ruta. ¡Buen trabajo!')}
          {filtro === 'proximas' && 'No tenés próximas asignadas para esta fecha.'}
          {filtro === 'hechas' && 'Todavía no hiciste ninguna.'}
          {filtro === 'todas' && 'No tenés entregas asignadas para esta fecha.'}
        </div>
      ) : (
        <div className="rep-list">
          <div className="rep-list-title">
            🗺️ Ruta · {paradas.filter(p => p.entregas.length > 0).length} parada{paradas.length !== 1 ? 's' : ''}
          </div>
          {paradas.map((p) => (
            <ParadaCard
              key={p.numero}
              parada={p}
              esProxima={proximaParada && proximaParada.numero === p.numero}
              navigate={navigate}
              nombreRepartidor={nombreRepartidor}
            />
          ))}
        </div>
      )}

      {showDeposito && (
        <DepositoModal
          actual={deposito}
          onClose={() => setShowDeposito(false)}
          onSaved={async () => { setShowDeposito(false); await cargarTodo() }}
        />
      )}

      {showScanner && (
        <QRScanner
          onDetect={onQRDetectado}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Toast de error del scanner */}
      {scanError && (
        <div className="rep-toast">{scanError}</div>
      )}

      {/* Botón flotante para escanear QR (FAB) */}
      <button
        className="rep-fab-scan"
        onClick={() => setShowScanner(true)}
        aria-label="Escanear QR"
      >
        📷 <span>Escanear QR</span>
      </button>

      <style>{styles}</style>
    </div>
  )
}

/* ───────── Próxima parada (HERO destacado arriba) ───────── */
function ProximaParadaCard({ parada, navigate, nombreRepartidor }) {
  const pendientes = parada.entregas.filter(e => e.estado === 'programada' || e.estado === 'en_ruta')
  if (pendientes.length === 0) return null
  const dirRef = pendientes[0].cliente
  const dirEncoded = encodeURIComponent(`${dirRef?.direccion || ''}, ${dirRef?.barrio || ''}, Montevideo, Uruguay`)
  const wazeUrl = parada.centroide.lat
    ? `https://www.waze.com/ul?ll=${parada.centroide.lat}%2C${parada.centroide.lng}&navigate=yes`
    : `https://www.waze.com/ul?q=${dirEncoded}&navigate=yes`
  const km = parada.distanciaDesdeAnterior ? (parada.distanciaDesdeAnterior / 1000).toFixed(1) : null

  // WA al primer cliente de la parada (si son varios, abre uno por uno)
  const cliente0 = pendientes[0]?.cliente
  const recibe0 = cliente0?.beneficiario || cliente0?.titular
  const tel0Raw = (recibe0?.phone || '').replace(/[^\d]/g, '')
  const tel0Wa = tel0Raw ? (tel0Raw.startsWith('598') ? tel0Raw : '598' + tel0Raw.replace(/^0+/, '')) : null
  const nombre0 = recibe0?.nombre?.split(' ')[0] || ''
  const waMsg0 = encodeURIComponent(
    `Hola ${nombre0}! Soy ${nombreRepartidor || 'tu repartidor'} de *Limpack*. Estoy yendo con tu pedido, llego en unos minutos.`
  )
  const waUrl0 = tel0Wa ? `https://wa.me/${tel0Wa}?text=${waMsg0}` : null

  // Token de la primera entrega para "Entregado" (ir a confirmar)
  const tokenEntregar = pendientes[0]?.tracking_token

  // Datos del cliente para mostrar arriba
  const nombreClienteParada = recibe0?.nombre || ''
  const direccionParada = `${cliente0?.direccion || ''}${cliente0?.apto ? ` · ${cliente0.apto}` : ''}`

  // Identificador del paquete: últimos 3 dígitos del numero_orden + total de bultos
  // Si hay varias entregas en la parada, mostramos todos
  return (
    <div className="rep-prox">
      <div className="rep-prox-tag">PRÓXIMA ENTREGA</div>

      {/* Strip de IDs de paquetes — bien visible para que el repartidor agarre el bulto correcto */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {pendientes.map(e => {
          const idCorto = (e.numero_orden || '').split('-').pop() || '?'
          return (
            <div key={e.id} style={{
              background: '#fff', color: '#0F1F3D',
              padding: '8px 14px', borderRadius: 10,
              fontWeight: 900, fontSize: 18, letterSpacing: '.04em',
              display: 'flex', alignItems: 'center', gap: 8,
              border: '2px solid rgba(255,255,255,.4)',
            }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>PAQUETE</span>
              <span>#{idCorto}</span>
              <span style={{
                background: '#0EA5E9', color: '#fff',
                padding: '2px 8px', borderRadius: 999,
                fontSize: 11, fontWeight: 800,
              }}>
                {e.bultos_total || 1} {(e.bultos_total || 1) === 1 ? 'bulto' : 'bultos'}
              </span>
            </div>
          )
        })}
      </div>

      <div className="rep-prox-head">
        <div className="rep-prox-bnum">{parada.entregas.length}</div>
        <div>
          <div className="rep-prox-titulo">{nombreClienteParada}</div>
          <div className="rep-prox-barrio">{direccionParada}</div>
          <div className="rep-prox-km" style={{ marginTop: 4 }}>
            {parada.label}{km ? ` · 📍 a ~${km} km` : ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
        <a href={wazeUrl} target="_blank" rel="noopener" className="rep-btn-ir">
          🧭 IR CON WAZE
        </a>
        {waUrl0 ? (
          <a href={waUrl0} target="_blank" rel="noopener" className="rep-btn-ir" style={{ background: '#25D366', color: '#fff' }}>
            📲 AVISAR POR WHATSAPP
          </a>
        ) : (
          <button className="rep-btn-ir" disabled style={{ background: '#94A3B8', color: '#fff', cursor: 'not-allowed' }}>
            📲 SIN WHATSAPP
          </button>
        )}
        {tokenEntregar && (
          <button onClick={() => navigate(`/d/${tokenEntregar}`)}
            className="rep-btn-ir"
            style={{ background: '#10B981', color: '#fff', border: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
            ✓ MARCAR ENTREGADO
          </button>
        )}
      </div>
    </div>
  )
}

/* ───────── Parada (puede tener N entregas) ───────── */
function ParadaCard({ parada, esProxima, navigate, nombreRepartidor }) {
  if (parada.entregas.length === 0) return null
  const múltiple = parada.entregas.length > 1
  const dirRef = parada.entregas[0].cliente
  const dirEncoded = encodeURIComponent(`${dirRef?.direccion || ''}, ${dirRef?.barrio || ''}, Montevideo, Uruguay`)
  const wazeUrl = parada.centroide.lat
    ? `https://www.waze.com/ul?ll=${parada.centroide.lat}%2C${parada.centroide.lng}&navigate=yes`
    : `https://www.waze.com/ul?q=${dirEncoded}&navigate=yes`
  const km = parada.distanciaDesdeAnterior ? (parada.distanciaDesdeAnterior / 1000).toFixed(1) : null

  return (
    <div className={`rep-parada ${esProxima ? 'es-prox' : ''} ${múltiple ? 'multi' : ''}`}>
      <div className="rep-parada-head">
        <div className="rep-parada-num">P{parada.numero}</div>
        <div className="rep-parada-info">
          <div className="rep-parada-titulo">
            {múltiple ? `${parada.entregas.length} entregas` : '1 entrega'}
            {parada.label && <span className="rep-parada-barrio"> · {parada.label}</span>}
          </div>
          {km && <div className="rep-parada-km">📍 a ~{km} km del punto anterior</div>}
        </div>
        <a href={wazeUrl} target="_blank" rel="noopener" className="rep-parada-waze" title="Ir con Waze">🧭</a>
      </div>

      <div className="rep-parada-body">
        {parada.entregas.map((e, i) => (
          <EntregaItem key={e.id} e={e} mostrarCheck={múltiple} navigate={navigate} nombreRepartidor={nombreRepartidor} />
        ))}
      </div>
    </div>
  )
}

/* ───────── Item individual de entrega ───────── */
function EntregaItem({ e, mostrarCheck, navigate, nombreRepartidor }) {
  // Quien recibe físicamente: beneficiario si existe, sino el titular
  const recibe = e.cliente?.beneficiario || e.cliente?.titular
  const titular = e.cliente?.titular
  const esRegalo = e.cliente?.beneficiario && e.cliente?.beneficiario?.id !== e.cliente?.titular?.id

  // WhatsApp: al beneficiario (quien recibe). Solo dígitos, prefijo 598 si falta
  const telRaw = (recibe?.phone || '').replace(/[^\d]/g, '')
  const telWa = telRaw ? (telRaw.startsWith('598') ? telRaw : '598' + telRaw.replace(/^0+/, '')) : null
  const nombreCliente = recibe?.nombre?.split(' ')[0] || ''
  const waMsg = encodeURIComponent(
    `Hola ${nombreCliente}, ¿cómo estás? Soy ${nombreRepartidor || 'tu repartidor'} de Limpack, te aviso que estoy yendo con tu pedido 🙂`
  )
  const waUrl = telWa ? `https://wa.me/${telWa}?text=${waMsg}` : null
  const yaHecha = e.estado === 'entregada' || e.estado === 'no_entregada' || e.estado === 'cancelada'

  const estadoCfg = {
    programada: { label: 'PEND.', color: '#0EA5E9', bg: '#E0F2FE' },
    en_ruta:    { label: 'EN RUTA', color: '#92400E', bg: '#FEF3C7' },
    entregada:  { label: 'ENTREGADA', color: '#065F46', bg: '#D1FAE5' },
    no_entregada: { label: 'NO ENT.', color: '#991B1B', bg: '#FEE2E2' },
    cancelada:  { label: 'CANCEL.', color: '#475569', bg: '#F1F5F9' },
  }
  const cfg = estadoCfg[e.estado] || estadoCfg.programada

  return (
    <div className={`rep-ent ${yaHecha ? 'hecha' : ''}`}>
      <div className="rep-ent-row">
        <div className="rep-ent-info">
          <div className="rep-ent-nombre">{recibe?.nombre || '—'}</div>
          {esRegalo && (
            <div style={{ fontSize: 10, color: '#7C3AED', marginTop: 1, fontWeight: 700 }}>
              🎁 contrata: {titular?.nombre}
            </div>
          )}
          {e.cliente?.alias && (
            <div style={{ fontSize: 10, fontWeight: 800, color: '#0F1F3D', marginTop: 1, letterSpacing: '.05em' }}>
              📍 {e.cliente.alias.toUpperCase()}
            </div>
          )}
          <div className="rep-ent-dir">
            {e.cliente?.direccion}{e.cliente?.apto ? ` · ${e.cliente.apto}` : ''}
            {e.cliente?.tipo_vivienda && <span className="rep-ent-viv">{VIVIENDA[e.cliente.tipo_vivienda]}</span>}
          </div>
        </div>
        <div className="rep-ent-bultos">
          <div className="rep-ent-bnum">{e.bultos_total || 1}</div>
          <div className="rep-ent-blab">B</div>
        </div>
      </div>

      <div className="rep-ent-meta">
        <span className="rep-pill" style={{ background: cfg.bg, color: cfg.color }}>● {cfg.label}</span>
        <span style={{
          background: '#0F1F3D', color: '#fff',
          padding: '3px 10px', borderRadius: 6,
          fontSize: 12, fontWeight: 900, letterSpacing: '.04em',
        }}>
          📦 #{(e.numero_orden || '').split('-').pop() || '?'}
        </span>
        <span className="rep-ent-kit">{e.kit?.nombre || '—'}</span>
      </div>

      {e.notas && <div className="rep-notas">★ {e.notas}</div>}

      {!yaHecha && (
        <div className="rep-ent-actions">
          {waUrl ? (
            <a href={waUrl} target="_blank" rel="noopener" className="rep-mini rep-mini-wa">💬 WhatsApp</a>
          ) : <button className="rep-mini" disabled>💬 —</button>}
          <button className="rep-mini rep-mini-ok" onClick={() => navigate(`/d/${e.tracking_token}`)}>
            ✓ Entregar
          </button>
        </div>
      )}
    </div>
  )
}

/* ───────── Modal configurar depósito (geocoding inline) ───────── */
function DepositoModal({ actual, onClose, onSaved }) {
  const [direccion, setDireccion] = useState(actual?.direccion || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setBusy(true)
    try {
      const geo = await geocodificar(direccion)
      if (!geo) throw new Error('No pude encontrar esa dirección. Probá con calle + número + barrio.')
      const { error } = await supabase.rpc('set_deposito', { direccion_p: direccion, lat_p: geo.lat, lng_p: geo.lng })
      if (error) throw error
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  return (
    <div className="rep-modal-bg" onClick={onClose}>
      <div className="rep-modal" onClick={ev => ev.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px' }}>📍 Punto de partida</h2>
        <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
          Tu casa o depósito de donde salís a hacer las entregas. Sirve para optimizar la ruta.
        </p>
        <input
          type="text"
          value={direccion}
          onChange={ev => setDireccion(ev.target.value)}
          placeholder="Ej: Av. Italia 1234, Pocitos"
          style={{ width: '100%', padding: 12, fontSize: 15, borderRadius: 8, border: '1px solid #ccc' }}
        />
        {err && <div style={{ color: '#991B1B', fontSize: 13, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn ghost">Cancelar</button>
          <button onClick={guardar} disabled={busy || !direccion.trim()} className="btn primary">
            {busy ? 'Buscando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = `
:root {
  --rep-pri: #0F1F3D;
  --rep-acc: #00A8B5;
  --rep-warn: #F59E0B;
  --rep-bg: #F4F5F7;
  --rep-card: #fff;
  --rep-line: #E5E7EB;
  --rep-mut: #6B7280;
}
body { background: var(--rep-bg); margin: 0; font-family: 'Inter', sans-serif; }
.rep-app { max-width: 480px; margin: 0 auto; padding-bottom: 100px; color: #111 }
.rep-head {
  position: sticky; top: 0; z-index: 10;
  background: var(--rep-pri); color: #fff;
  padding: 14px 16px;
  display: flex; justify-content: space-between; align-items: center;
}
.rep-brand { font-size: 18px; font-weight: 900; letter-spacing: -.02em }
.rep-sub { font-size: 12px; opacity: .8; margin-top: 2px }
.rep-logout { background: rgba(255,255,255,.15); color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600 }
.rep-fecha { background: #fff; padding: 12px 16px; border-bottom: 1px solid var(--rep-line); display: flex; justify-content: space-between; align-items: center; gap: 10px }
.rep-fecha-label { font-size: 13px; font-weight: 700; color: var(--rep-pri) }
.rep-fecha-input { font-size: 13px; padding: 6px 10px; border: 1px solid var(--rep-line); border-radius: 6px; background: #fff; font-family: inherit }
.rep-warn-bar { background: #FEF3C7; color: #92400E; padding: 10px 16px; font-size: 13px; font-weight: 600; cursor: pointer; border-bottom: 1px solid #F59E0B }
.rep-info-bar { background: #E0F2FE; color: #0C4A6E; padding: 10px 16px; font-size: 13px; display: flex; justify-content: space-between; align-items: center; gap: 8px; border-bottom: 1px solid #0EA5E9 }
.rep-link-btn { background: transparent; border: 0; color: #0C4A6E; text-decoration: underline; font-weight: 700; font-size: 13px; cursor: pointer; padding: 0 }
.rep-link-btn:disabled { opacity: .5 }
.rep-tabs { background: #fff; padding: 6px; border-bottom: 1px solid var(--rep-line); display: flex; gap: 4px }
.rep-tab { flex: 1; background: transparent; border: 0; padding: 10px 4px; border-radius: 8px; font-size: 13px; font-weight: 700; color: var(--rep-mut); cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 2px }
.rep-tab.active { background: var(--rep-pri); color: #fff }
.rep-tab-num { font-size: 16px; font-weight: 900 }
.rep-action-bar { background: var(--rep-bg); padding: 10px 12px }
.rep-btn-ruta { width: 100%; background: var(--rep-pri); color: #fff; border: 0; padding: 14px; border-radius: 10px; font-size: 14px; font-weight: 800; box-shadow: 0 4px 12px rgba(15,31,61,.25); cursor: pointer }
.rep-btn-ruta:disabled { opacity: .5 }
.rep-empty { text-align: center; padding: 60px 20px; color: var(--rep-mut); font-size: 14px }

/* HERO próxima parada */
.rep-prox {
  margin: 12px;
  background: linear-gradient(135deg, #00A8B5 0%, #0F1F3D 100%);
  color: #fff; border-radius: 16px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 168, 181, .25);
}
.rep-prox-tag { font-size: 11px; font-weight: 800; letter-spacing: .15em; opacity: .8; margin-bottom: 6px }
.rep-prox-head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px }
.rep-prox-bnum {
  font-size: 38px; font-weight: 900; line-height: 1;
  background: rgba(255,255,255,.2); padding: 8px 16px; border-radius: 12px;
  letter-spacing: -.05em;
}
.rep-prox-titulo { font-size: 17px; font-weight: 800; line-height: 1.15 }
.rep-prox-barrio { font-size: 13px; opacity: .9; margin-top: 2px; text-transform: uppercase; letter-spacing: .08em; font-weight: 700 }
.rep-prox-km { font-size: 12px; opacity: .85; margin-top: 4px }
.rep-btn-ir {
  display: block; width: 100%; box-sizing: border-box;
  background: #33CCFF; color: #002B4D;
  text-align: center; text-decoration: none;
  padding: 16px; border-radius: 12px;
  font-size: 17px; font-weight: 900; letter-spacing: .05em;
  box-shadow: 0 4px 16px rgba(51,204,255,.5);
}

/* Lista de paradas */
.rep-list { padding: 12px; display: flex; flex-direction: column; gap: 10px }
.rep-list-title { font-size: 12px; font-weight: 800; color: var(--rep-mut); text-transform: uppercase; letter-spacing: .12em; padding: 4px 4px 0 }
.rep-parada {
  background: var(--rep-card); border-radius: 14px;
  padding: 12px; box-shadow: 0 1px 3px rgba(0,0,0,.06);
  border: 2px solid transparent;
}
.rep-parada.es-prox { border-color: var(--rep-acc); display: none } /* Si está como hero arriba, no la repetimos abajo */
.rep-parada.multi { background: linear-gradient(180deg, #fff 0%, #F0F9FF 100%); border-color: #0EA5E9 }
.rep-parada-head { display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center; margin-bottom: 10px }
.rep-parada-num {
  font-size: 13px; font-weight: 900;
  background: var(--rep-pri); color: #fff;
  padding: 6px 10px; border-radius: 8px;
  letter-spacing: -.02em;
}
.rep-parada-titulo { font-size: 14px; font-weight: 800; line-height: 1.15 }
.rep-parada-barrio { color: var(--rep-mut); font-weight: 600 }
.rep-parada-km { font-size: 11px; color: var(--rep-mut); margin-top: 2px }
.rep-parada-waze {
  display: inline-flex; align-items: center; justify-content: center;
  width: 42px; height: 42px;
  background: #33CCFF; color: #002B4D; text-decoration: none;
  border-radius: 10px; font-size: 22px;
}
.rep-parada-body { display: flex; flex-direction: column; gap: 8px }

/* Item de entrega */
.rep-ent { padding: 10px; background: rgba(0,0,0,.02); border-radius: 8px; border: 1px solid rgba(0,0,0,.06) }
.rep-ent.hecha { opacity: .55 }
.rep-ent-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: flex-start }
.rep-ent-nombre { font-size: 15px; font-weight: 800; line-height: 1.15 }
.rep-ent-dir { font-size: 13px; margin-top: 3px; color: #333 }
.rep-ent-viv { font-size: 9px; font-weight: 800; letter-spacing: .08em; padding: 2px 5px; background: #111; color: #fff; border-radius: 3px; margin-left: 6px }
.rep-ent-bultos { text-align: center; background: #111; color: #fff; padding: 4px 8px; border-radius: 6px; min-width: 34px }
.rep-ent-bnum { font-size: 17px; font-weight: 900; line-height: 1 }
.rep-ent-blab { font-size: 7px; font-weight: 700; letter-spacing: .15em; margin-top: 1px }
.rep-ent-meta { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12px }
.rep-pill { padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; letter-spacing: .05em }
.rep-ent-kit { color: var(--rep-mut); font-weight: 600 }
.rep-notas { margin-top: 6px; padding: 6px 8px; background: #FEF3C7; border-left: 3px solid var(--rep-warn); border-radius: 4px; font-size: 12px }
.rep-ent-actions { margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 6px }
.rep-mini { padding: 10px 6px; border-radius: 8px; font-size: 13px; font-weight: 700; border: 0; cursor: pointer; text-align: center; text-decoration: none; color: #fff; display: block }
.rep-mini:disabled { opacity: .4; background: #ccc }
.rep-mini-wa { background: #25D366 }
.rep-mini-ok { background: var(--rep-pri) }

/* FAB (botón flotante) escanear QR */
.rep-fab-scan {
  position: fixed;
  bottom: 20px;
  right: 50%; transform: translateX(min(228px, 45vw));
  background: linear-gradient(135deg, #00A8B5 0%, #0F1F3D 100%);
  color: #fff; border: 0;
  padding: 14px 20px;
  border-radius: 999px;
  font-size: 15px; font-weight: 800; letter-spacing: .02em;
  box-shadow: 0 8px 24px rgba(0, 168, 181, .4);
  cursor: pointer;
  display: flex; align-items: center; gap: 8px;
  z-index: 50;
}
.rep-fab-scan:active { transform: translateX(min(228px, 45vw)) scale(.96) }

/* Toast de error del scanner */
.rep-toast {
  position: fixed; bottom: 90px; left: 16px; right: 16px;
  max-width: 460px; margin: 0 auto;
  background: #FEE2E2; color: #991B1B;
  padding: 14px 16px; border-radius: 10px;
  font-size: 14px; font-weight: 600;
  border: 1.5px solid #FCA5A5;
  box-shadow: 0 4px 16px rgba(0,0,0,.15);
  z-index: 60;
}

/* Modal */
.rep-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px }
.rep-modal { background: #fff; border-radius: 14px; padding: 20px; max-width: 420px; width: 100% }
`
